/**
 * Obstruction Wizard Submission
 *
 * Wraps the create/update flow: validates the whole form, maps to the DTO,
 * dispatches the slice action, navigates to the new detail panel on success.
 */
import { useCallback, useState } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/hooks/useNotifications';
import { useGeoMarkupStore } from '@/store';
import { obstructionAttachmentsService } from '@/services/api';
import type { Coordinates } from '@/types';
import type {
  CreateObstructionContractorData,
  CreateObstructionData,
  Obstruction,
  UpdateObstructionData,
} from '@/types/obstructions';

import type { ObstructionWizardFormState } from '../types';
import { OBSTRUCTION_STEP_LOGIC } from '../utils/validation/stepValidation';

interface UseObstructionWizardSubmissionProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  editingObstruction: Obstruction | null;
  anchorPoint: Coordinates | null;
  forceClose: () => void;
  goToStep: (step: number) => boolean;
}

/** Identify the first step (1..3) whose required fields are missing/invalid. */
function findFirstInvalidStep(
  values: ObstructionWizardFormState,
  formErrors: Partial<Record<keyof ObstructionWizardFormState, string>>,
  anchorPoint: Coordinates | null,
  t: (key: string, options?: Record<string, unknown>) => string
): number | null {
  const valuesWithAnchor: ObstructionWizardFormState = {
    ...values,
    anchorPoint,
  };
  // Validate every section in order; first invalid one wins.
  for (let index = 0; index < OBSTRUCTION_STEP_LOGIC.length; index += 1) {
    const result = OBSTRUCTION_STEP_LOGIC[index]?.validate({
      values: valuesWithAnchor,
      formErrors,
      t,
    });
    if (result && !result.isValid) return index + 1;
  }
  // Mantine-level field errors that aren't required-for-navigation.
  const allFieldsWithErrors = (
    Object.keys(formErrors) as (keyof ObstructionWizardFormState)[]
  ).filter((key) => formErrors[key]);
  if (allFieldsWithErrors.length === 0) return null;
  const step1Fields = new Set([
    ...(OBSTRUCTION_STEP_LOGIC[0]?.requiredFields ?? []),
    'location' as keyof ObstructionWizardFormState,
    'description' as keyof ObstructionWizardFormState,
  ]);
  const step2Fields = new Set(OBSTRUCTION_STEP_LOGIC[1]?.requiredFields ?? []);
  const step3Fields = new Set([
    'affectedRoads' as keyof ObstructionWizardFormState,
  ]);
  if (allFieldsWithErrors.some((field) => step1Fields.has(field))) return 1;
  if (allFieldsWithErrors.some((field) => step2Fields.has(field))) return 2;
  if (allFieldsWithErrors.some((field) => step3Fields.has(field))) return 3;
  return null;
}

/** Trim and return `null` for empty strings — what the backend expects for optional text fields. */
function nullableTrim(value: string): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length === 0 ? null : trimmed;
}

function buildContractor(
  values: ObstructionWizardFormState
): CreateObstructionContractorData {
  return {
    company: values.contractorCompany.trim(),
    manager: values.contractorManager.trim(),
    phone: values.contractorPhone.trim(),
    email: nullableTrim(values.contractorEmail),
    emergencyContact: nullableTrim(values.contractorEmergencyContact),
    permitNumber: nullableTrim(values.contractorPermitNumber),
    permitIssuedDate: nullableTrim(values.contractorPermitIssuedDate),
    permitExpiryDate: nullableTrim(values.contractorPermitExpiryDate),
    notes: nullableTrim(values.contractorNotes),
  };
}

/** Map the wizard's affected-road inputs to the create/update payload shape. */
function buildAffectedRoads(
  values: ObstructionWizardFormState
): { location: string; description: string | null }[] {
  return values.affectedRoads.map((item) => ({
    location: item.location.trim(),
    description: item.description.trim() ? item.description.trim() : null,
  }));
}

/** Upload newly selected files to the obstruction after it is created/updated. */
async function uploadAttachments(
  obstructionId: string,
  files: File[]
): Promise<void> {
  if (files.length === 0) return;
  await Promise.all(
    files.map((file) =>
      obstructionAttachmentsService.upload(obstructionId, file)
    )
  );
}

export interface UseObstructionWizardSubmissionReturn {
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
  handleDeleteExistingAttachment: (attachmentId: string) => Promise<void>;
}

export function useObstructionWizardSubmission({
  form,
  editingObstruction,
  anchorPoint,
  forceClose,
  goToStep,
}: UseObstructionWizardSubmissionProps): UseObstructionWizardSubmissionReturn {
  const { t } = useTranslation();
  const { showError, showWarning, showSuccess } = useNotifications();

  const createObstruction = useGeoMarkupStore(
    (state) => state.obstructions.createObstruction
  );
  const updateObstruction = useGeoMarkupStore(
    (state) => state.obstructions.updateObstruction
  );
  const selectItem = useGeoMarkupStore(
    (state) => state.obstructions.selectItem
  );
  const openDetails = useGeoMarkupStore(
    (state) => state.obstructions.openDetails
  );
  const fetchObstructionByShortId = useGeoMarkupStore(
    (state) => state.obstructions.fetchObstructionByShortId
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Upload any newly-selected attachments for the given obstruction, then
   * refresh the detail record so the panel reflects the new files.
   *
   * uploadAttachments already early-returns on an empty list, so no outer
   * length-check is needed here.
   *
   * Intentionally swallows upload errors: the obstruction was already
   * saved — a failed upload must never be reported as a save failure.
   */
  const uploadPendingAttachments = useCallback(
    async (obstructionId: string): Promise<void> => {
      try {
        await uploadAttachments(obstructionId, form.values.attachments);
        await fetchObstructionByShortId(obstructionId);
      } catch (uploadError) {
        console.error(
          '[useObstructionWizardSubmission] attachment upload failed',
          uploadError
        );
        showWarning(
          t('obstructions.wizard.notifications.attachmentsUploadFailed')
        );
      }
    },
    [form, fetchObstructionByShortId, showWarning, t]
  );

  const handleSubmit = useCallback(async () => {
    const validation = form.validate();
    if (validation.hasErrors) {
      const firstInvalidStep = findFirstInvalidStep(
        form.values,
        validation.errors as Partial<
          Record<keyof ObstructionWizardFormState, string>
        >,
        anchorPoint,
        t
      );
      if (firstInvalidStep) {
        goToStep(firstInvalidStep);
        showWarning(
          t('obstructions.wizard.validation.fixErrorsInStep', {
            step: firstInvalidStep,
          })
        );
      }
      return;
    }

    // The anchor is placed on the map BEFORE the form opens; there is no
    // geometry step to jump to. A missing anchor means an unexpected state —
    // notify and abort rather than navigating to a non-existent step.
    if (!anchorPoint) {
      showWarning(t('obstructions.wizard.validation.anchorRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const startDate = new Date(form.values.plannedStartDate);
      const endDate = new Date(form.values.plannedEndDate);

      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
      ) {
        goToStep(1);
        showWarning(t('obstructions.wizard.validation.endAfterStart'));
        setIsSubmitting(false);
        return;
      }

      if (editingObstruction) {
        const updateData: UpdateObstructionData = {
          title: form.values.title.trim(),
          description: form.values.description.trim(),
          type: form.values.type,
          severity: form.values.severity,
          impact: form.values.impact,
          location: nullableTrim(form.values.location),
          plannedStartDate: startDate,
          plannedEndDate: endDate,
          contractor: buildContractor(form.values),
          affectedRoads: buildAffectedRoads(form.values),
          anchorPoint,
        };
        const success = await updateObstruction(
          editingObstruction.id,
          updateData
        );
        if (!success) {
          showError(t('obstructions.wizard.notifications.updateFailed'));
          setIsSubmitting(false);
          return;
        }
        // Upload any newly selected files, then refresh the detail so the
        // attachments collection reflects the new uploads.
        // The obstruction was already saved — upload failures are downgraded
        // to warnings inside uploadPendingAttachments; they never bubble here.
        await uploadPendingAttachments(editingObstruction.id);
        showSuccess(t('obstructions.wizard.notifications.updateSuccess'));
        forceClose();
        return;
      }

      const createData: CreateObstructionData = {
        title: form.values.title.trim(),
        description: form.values.description.trim(),
        type: form.values.type,
        severity: form.values.severity,
        impact: form.values.impact,
        location: nullableTrim(form.values.location),
        internalNotes: null,
        plannedStartDate: startDate,
        plannedEndDate: endDate,
        contractor: buildContractor(form.values),
        affectedRoads: buildAffectedRoads(form.values),
        anchorPoint,
      };

      const newId = await createObstruction(createData);
      if (!newId) {
        showError(t('obstructions.wizard.notifications.createFailed'));
        setIsSubmitting(false);
        return;
      }

      // Upload any newly selected files, then refresh the detail so the
      // attachments collection is hydrated before the panel opens.
      // The obstruction was already created — upload failures are downgraded
      // to warnings inside uploadPendingAttachments; they never bubble here.
      await uploadPendingAttachments(newId);
      showSuccess(t('obstructions.wizard.notifications.createSuccess'));
      selectItem(newId);
      openDetails();
      forceClose();
    } catch (error) {
      console.error(
        '[useObstructionWizardSubmission] submission failed',
        error
      );
      const message = editingObstruction
        ? t('obstructions.wizard.notifications.updateFailed')
        : t('obstructions.wizard.notifications.createFailed');
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    editingObstruction,
    anchorPoint,
    forceClose,
    goToStep,
    createObstruction,
    updateObstruction,
    uploadPendingAttachments,
    selectItem,
    openDetails,
    showError,
    showWarning,
    showSuccess,
    t,
  ]);

  const handleDeleteExistingAttachment = useCallback(
    async (attachmentId: string) => {
      if (!editingObstruction) return;
      try {
        await obstructionAttachmentsService.delete(
          editingObstruction.id,
          attachmentId
        );
        // Refresh detail so the removed attachment disappears from the list.
        await fetchObstructionByShortId(editingObstruction.id);
      } catch (error) {
        console.error(
          '[useObstructionWizardSubmission] attachment delete failed',
          error
        );
        showError(t('obstructions.wizard.notifications.updateFailed'));
      }
    },
    [editingObstruction, fetchObstructionByShortId, showError, t]
  );

  return {
    isSubmitting,
    handleSubmit,
    handleDeleteExistingAttachment,
  };
}
